import React, { useState } from 'react';
import '../Login/LoginShop.css';
import './ProviderLogin.css';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { BACKEND_URL } from '../../services/Helper';
import { useGoogleLogin } from '@react-oauth/google';

const notifyB = (msg) => toast.success(msg);
const notifyA = (msg) => toast.error(msg);

const ProviderLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [providerData, setProviderData] = useState(null);

  const handleGetOtp = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      notifyA('Please enter a valid email');
      return;
    }
    try {
      setLoading(true);
      const res = await fetch(`${BACKEND_URL}/nearbe/provider/sendotp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (!res.ok) return notifyA(data.error || 'Failed to send OTP');
      setOtpSent(true);
      notifyB('OTP sent to your email');
    } catch (error) {
      console.error(error);
      notifyA('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!otp || otp.length !== 6) {
      notifyA('OTP should be 6 digits');
      return;
    }
    try {
      setLoading(true);
      const res = await fetch(`${BACKEND_URL}/nearbe/provider/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp }),
      });
      const data = await res.json();
      if (!res.ok) return notifyA(data.error || 'Login failed');
      localStorage.setItem('providerlogintoken', data.token);
      setProviderData(data.provider);
      notifyB('Login Successful');
    } catch (error) {
      console.error(error);
      notifyA('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async (codeResponse) => {
    try {
      setLoading(true);
      const res = await fetch(`${BACKEND_URL}/nearbe/provider/googleloginmobile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accessToken: codeResponse.access_token }),
      });
      const data = await res.json();
      if (!res.ok) return notifyA(data.error || 'Google login failed');
      localStorage.setItem('providerlogintoken', data.token);
      setProviderData(data.provider);
      notifyB('Login Successful');
    } catch (error) {
      console.error(error);
      notifyA('Google login failed');
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = useGoogleLogin({
    onSuccess: handleGoogleLogin,
    onError: () => notifyA('Google login failed'),
    flow: 'implicit',
  });

  const handleLogout = () => {
    localStorage.removeItem('providerlogintoken');
    setProviderData(null);
    setEmail('');
    setOtp('');
    setOtpSent(false);
  };

  if (providerData) {
    return (
      <div className="loginshop-container">
        <div className="provider-dashboard">
          <div className="provider-avatar">🔧</div>
          <h2>{providerData.name}</h2>
          <p className="provider-profession">{providerData.profession}</p>
          {providerData.isVerified && <span className="badge verified">✓ Verified</span>}
          {providerData.isFeatured && <span className="badge featured">⭐ Featured</span>}

          <div className="provider-stats">
            {providerData.avgRating > 0 && (
              <div className="stat-row">
                <span>Rating</span>
                <span>★ {providerData.avgRating} ({providerData.totalReviews} reviews)</span>
              </div>
            )}
            {providerData.experience && (
              <div className="stat-row">
                <span>Experience</span>
                <span>{providerData.experience}</span>
              </div>
            )}
            {providerData.feeRange && (
              <div className="stat-row">
                <span>Fee Range</span>
                <span>{providerData.feeRange}</span>
              </div>
            )}
            {providerData.availability && (
              <div className="stat-row">
                <span>Availability</span>
                <span>{providerData.availability}</span>
              </div>
            )}
            {providerData.contactNumber && (
              <div className="stat-row">
                <span>Contact</span>
                <span>{providerData.contactNumber}</span>
              </div>
            )}
            {providerData.areasServed?.length > 0 && (
              <div className="stat-row">
                <span>Areas</span>
                <span>{providerData.areasServed.join(', ')}</span>
              </div>
            )}
          </div>

          {providerData.description && (
            <p className="provider-desc">{providerData.description}</p>
          )}

          <p className="provider-manage-hint">
            To update your profile details, contact the Nearbeee team or use the mobile app.
          </p>

          <button className="loginshop-btn" onClick={handleLogout} style={{ marginTop: 16 }}>
            Logout
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="loginshop-container">
      <form className="loginshop-form" onSubmit={otpSent ? handleLogin : handleGetOtp}>
        <h2>Provider Login</h2>
        <p style={{ fontSize: 12, color: '#888', marginBottom: 8, textAlign: 'center' }}>
          Not registered? Register via the Services page.
        </p>

        <input
          type="email"
          placeholder="Enter your registered email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />

        {otpSent && (
          <input
            type="text"
            placeholder="Enter OTP"
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            required
          />
        )}

        <button type="submit" className="loginshop-btn" disabled={loading}>
          {loading ? (
            <span className="spinner">
              <div className="spinner-circle"></div>
              {otpSent ? 'Logging in...' : 'Sending OTP...'}
            </span>
          ) : otpSent ? 'Login' : 'Get OTP'}
        </button>

        <div className="google-divider"><span>or</span></div>

        <button type="button" className="google-btn" onClick={() => googleLogin()} disabled={loading}>
          <img
            src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
            alt="Google"
            style={{ width: 20, height: 20 }}
          />
          Continue with Google
        </button>

        <p className="register-text" style={{ marginTop: 12 }}>
          Not registered?{' '}
          <button type="button" className="register-btn" onClick={() => navigate('/userdashboard/services')}>
            Register as Provider
          </button>
        </p>
      </form>
    </div>
  );
};

export default ProviderLogin;
